import { createCoupleSchema, type CreateCoupleSchema } from '@/api/couples/couple.schema';
import {
  CoupleFields,
  EMPTY_COUPLE,
  type CoupleFormValues,
} from '@/components/enc/couple-fields';
import { FormSection, TextField } from '@/components/enc/fields';
import { DashedNote } from '@/components/enc/primitives';
import {
  RETREAT_PLACE,
  RETREAT_PLACE_DETAIL,
  retreatInfo,
} from '@/components/enc/retreat';
import { SubscribeLayout } from '@/components/enc/subscribe-layout';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { useCreateCouple } from '@/services/couples';
import { useEvent } from '@/services/events';
import { zodResolver } from '@hookform/resolvers/zod';
import { Calendar, MapPin, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

const Page = () => {
  const navigate = useNavigate();
  const { data: nextEvent, isLoading } = useEvent('next');
  const { mutateAsync: subscribe, isPending } = useCreateCouple();
  const info = retreatInfo(nextEvent);

  const form = useForm<CoupleFormValues>({
    resolver: zodResolver(createCoupleSchema) as unknown as Resolver<CoupleFormValues>,
    defaultValues: EMPTY_COUPLE,
  });

  useEffect(() => {
    if (nextEvent) form.setValue('eventId', nextEvent.id);
  }, [nextEvent]);

  const handleSubmit = form.handleSubmit(
    async (data) => {
      try {
        await subscribe(data as unknown as CreateCoupleSchema);
        navigate('/inscricao-confirmada', {
          state: {
            name: [data.husbandName.split(' ')[0], data.wifeName.split(' ')[0]]
              .filter(Boolean)
              .join(' e '),
          },
        });
      } catch {
        // o toast de erro já é exibido pelo hook
      }
    },
    () => window.scrollTo({ top: 200, behavior: 'smooth' }),
  );

  const handleReset = () => form.reset({ ...EMPTY_COUPLE, eventId: nextEvent?.id ?? '' });

  return (
    <SubscribeLayout event={nextEvent}>
      <ContentLoader show={isLoading || isPending} />

      <form
        noValidate
        onSubmit={handleSubmit}
        className="mx-auto flex max-w-[1200px] flex-wrap items-start gap-6 px-6 pt-12 pb-24"
      >
        <div className="flex min-w-0 flex-[2_1_560px] flex-col gap-4">
          {!isLoading && !nextEvent && (
            <DashedNote icon={<Calendar />} className="bg-white">
              As inscrições para o próximo retiro ainda não estão abertas. Volte em breve.
            </DashedNote>
          )}

          <CoupleFields form={form} variant="public" />

          <FormSection eyebrow="04 • FAMÍLIA" title="Os filhos">
            <TextField
              label="Filhos (quantidade / idades)"
              placeholder="Ex: 2 filhos, 6 e 9 anos"
              {...form.register('childrenInfo')}
            />
          </FormSection>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <span className="text-[13px] text-muted">* Campos obrigatórios</span>
            <div className="flex gap-3">
              <Button type="button" size="lg" variant="outline" onClick={handleReset}>
                Limpar
              </Button>
              <Button type="submit" size="lg" disabled={isPending || !nextEvent}>
                Inscrever-nos
              </Button>
            </div>
          </div>
        </div>

        <aside className="sticky top-[88px] flex min-w-0 flex-[1_1_300px] flex-col gap-3">
          <div className="overflow-hidden rounded-[14px] border border-[rgba(11,11,12,.10)] bg-white">
            <img
              src="/assets/about-1.jpg"
              alt="Casal em oração no Retiro Enchei-vos"
              className="block h-40 w-full object-cover"
            />
            <div className="flex flex-col gap-[18px] p-6">
              <span className="font-display text-lg font-bold text-ink">
                Retiro de Casais Enchei-vos
              </span>
              <div className="flex items-start gap-3">
                <Calendar className="size-[18px] flex-none text-brand" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-ink">
                    {info.hasEvent
                      ? `${info.whenTitle} de ${info.year}`
                      : 'Datas em breve'}
                  </span>
                  {info.weekdays && (
                    <span className="text-[13px] text-[#4A4A4F]">{info.weekdays}</span>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="size-[18px] flex-none text-brand" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-ink">{RETREAT_PLACE}</span>
                  <span className="text-[13px] leading-normal text-[#4A4A4F]">
                    {RETREAT_PLACE_DETAIL}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DashedNote icon={<ShieldCheck />} className="border-[rgba(11,11,12,.22)] p-5">
            <span className="text-[#4A4A4F]">
              Os dados de vocês são usados apenas pela equipe do retiro para organizar o
              encontro.
            </span>
          </DashedNote>
        </aside>
      </form>
    </SubscribeLayout>
  );
};

export default Page;
